/**
 * 操作中表示の CSS 変数（正本: global.scss :root、active/pe/grid は JS が html に上書き）
 * CSS 一覧: src/components/styles/_grid-operation.scss
 */
export const SHEET_CHROME_ACTIVE_VAR = '--sheet-chrome-active';
/** pointer-events: none | auto（--sheet-chrome-active と同期） */
export const SHEET_CHROME_PE_VAR = '--sheet-chrome-pe';
/** フェード時間（SCSS は var() で参照） */
export const SHEET_CHROME_FADE_DURATION_VAR = '--sheet-chrome-fade-duration';
/** 操作終了後 active を 0 にするまでの待機（JS が getSheetChromeIdleMs で読む） */
export const SHEET_CHROME_IDLE_DELAY_VAR = '--sheet-chrome-idle-delay';

/** グリッドオーバーレイ用（grid-disabled 時は JS が常に 0） */
export const GRID_OPERATION_ACTIVE_VAR = '--grid-operation-active';

/** @deprecated getSheetChromeIdleMs() を使用（値は --sheet-chrome-idle-delay） */
export const GRID_OPERATION_IDLE_MS = 3000;

let cachedSheetChromeIdleMs: number | null = null;

/** レイヤー切替スライド中は chrome active を固定（チップ行・削除スロットの高さ差でコンテンツがずれないように） */
let layerSlideChromeLock = false;
let scheduleChromeSync: (() => void) | null = null;

export function setLayerSlideChromeLock(locked: boolean): void {
  layerSlideChromeLock = locked;
  const root = document.documentElement;
  if (locked) {
    root.classList.add('layer-slide-transition');
    applyOperationVars(true);
    scheduleChromeSync?.();
    return;
  }
  root.classList.remove('layer-slide-transition');
  if (scheduleChromeSync) {
    scheduleChromeSync();
    return;
  }
  if (isSheetChromeTriggerActive()) {
    applyOperationVars(true);
  } else {
    applyOperationVars(false);
  }
}

export function isLayerSlideChromeLocked(): boolean {
  return layerSlideChromeLock;
}

function parseCssTimeToMs(raw: string, fallbackMs: number): number {
  const v = raw.trim();
  if (!v) {
    return fallbackMs;
  }
  if (v.endsWith('ms')) {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : fallbackMs;
  }
  if (v.endsWith('s')) {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n * 1000 : fallbackMs;
  }
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : fallbackMs;
}

/** :root の --sheet-chrome-idle-delay を ms に変換 */
export function getSheetChromeIdleMs(): number {
  if (cachedSheetChromeIdleMs != null) {
    return cachedSheetChromeIdleMs;
  }
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    SHEET_CHROME_IDLE_DELAY_VAR
  );
  cachedSheetChromeIdleMs = parseCssTimeToMs(raw, GRID_OPERATION_IDLE_MS);
  return cachedSheetChromeIdleMs;
}

/** レイヤー行: ホバー・押下・名前編集中も表示対象 */
const LAYER_CALC_LAYER_ACTIVE_SELECTORS = [
  '.active-sheet-header-block .sheet-layer-calc-list:hover',
  '.active-sheet-header-block .sheet-layer-calc-list:focus-within',
  '.active-sheet-header-block .sheet-layer-calc-chip:hover',
  '.active-sheet-header-block .sheet-layer-calc-chip.is-editing',
  '.active-sheet-header-block .sheet-layer-calc-item:hover',
  '.active-sheet-header-block .sheet-layer-calc-item:active',
  '.active-sheet-header-block .sheet-layer-calc-item:focus',
  '.active-sheet-header-block .sheet-layer-calc-item-input:focus',
].join(', ');

/** 履歴パネル（undo/redo ボタン行・スナップショット一覧） */
const HISTORY_CHROME_ACTIVE_SELECTORS = [
  '.history:hover',
  '.history:focus-within',
].join(', ');

const LAYER_CALC_INTERACTION_SELECTOR =
  '.active-sheet-header-block .sheet-layer-calc-list .sheet-layer-calc-chip, ' +
  '.active-sheet-header-block .sheet-layer-calc-list .sheet-layer-calc-item, ' +
  '.active-sheet-header-block .sheet-layer-calc-list .layer-add-btn';

function isLayerCalcInteractionTarget(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest(LAYER_CALC_INTERACTION_SELECTOR);
}

function applyOperationVars(chromeActive: boolean): void {
  const root = document.documentElement;
  const gridDisabled = document.body.classList.contains('grid-disabled');
  root.style.setProperty(SHEET_CHROME_ACTIVE_VAR, chromeActive ? '1' : '0');
  root.style.setProperty(SHEET_CHROME_PE_VAR, chromeActive ? 'auto' : 'none');
  root.style.setProperty(
    GRID_OPERATION_ACTIVE_VAR,
    !gridDisabled && chromeActive ? '1' : '0'
  );
}

/** いま操作トリガーが立っているか（グリッド OFF でもレイヤー行・履歴等は true になりうる） */
export function isSheetChromeTriggerActive(): boolean {
  const html = document.documentElement;
  const body = document.body;

  if (html.classList.contains('undoredo') || html.classList.contains('slideshow')) {
    return true;
  }
  if (
    body.classList.contains('is-element-dragging') ||
    body.classList.contains('is-calculator-touching')
  ) {
    return true;
  }
  if (document.querySelector('.pending-pouch, .pending-group-wrapper')) {
    return true;
  }
  if (document.querySelector('.calculator-area.group-drawing-mode')) {
    return true;
  }
  if (document.querySelector('.group-wrapper.group-dragging')) {
    return true;
  }
  if (document.querySelector('.pouch-container:hover, .group-container:hover')) {
    return true;
  }
  if (document.querySelector(LAYER_CALC_LAYER_ACTIVE_SELECTORS)) {
    return true;
  }
  if (document.querySelector(HISTORY_CHROME_ACTIVE_SELECTORS)) {
    return true;
  }

  return false;
}

/** @deprecated isSheetChromeTriggerActive を使用 */
export function isGridOperationTriggerActive(): boolean {
  return isSheetChromeTriggerActive();
}

/** :root の CSS 変数を同期（App 起動時に1回） */
export function startGridOperationActiveSync(): () => void {
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let rafId: number | null = null;
  let isActiveApplied = false;

  const clearIdleTimer = () => {
    if (idleTimer != null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };

  const armIdleTimer = () => {
    clearIdleTimer();
    idleTimer = setTimeout(() => {
      idleTimer = null;
      applyOperationVars(false);
      isActiveApplied = false;
    }, getSheetChromeIdleMs());
  };

  const onLayerCalcInteraction = (target: EventTarget | null) => {
    if (!isLayerCalcInteractionTarget(target)) {
      return;
    }
    applyOperationVars(true);
    isActiveApplied = true;
    armIdleTimer();
    requestAnimationFrame(() => {
      if (isSheetChromeTriggerActive()) {
        clearIdleTimer();
      }
    });
  };

  const sync = () => {
    if (layerSlideChromeLock) {
      clearIdleTimer();
      applyOperationVars(true);
      isActiveApplied = true;
      return;
    }

    if (isSheetChromeTriggerActive()) {
      clearIdleTimer();
      applyOperationVars(true);
      isActiveApplied = true;
      return;
    }

    if (!isActiveApplied) {
      applyOperationVars(false);
      return;
    }

    if (idleTimer != null) {
      return;
    }

    armIdleTimer();
  };

  const scheduleSync = () => {
    if (rafId != null) {
      return;
    }
    rafId = requestAnimationFrame(() => {
      rafId = null;
      sync();
    });
  };

  scheduleChromeSync = scheduleSync;

  const observer = new MutationObserver(scheduleSync);

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    subtree: true,
  });
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });

  const onPointerDown = (e: PointerEvent) => {
    onLayerCalcInteraction(e.target);
    scheduleSync();
  };
  const onClick = (e: MouseEvent) => {
    onLayerCalcInteraction(e.target);
    scheduleSync();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') {
      return;
    }
    const target = e.target;
    if (!(target instanceof Element) || !target.matches('.sheet-layer-calc-item-input')) {
      return;
    }
    onLayerCalcInteraction(target);
    scheduleSync();
  };

  document.addEventListener('mousemove', scheduleSync, { passive: true });
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('mousedown', scheduleSync, { passive: true });
  document.addEventListener('mouseup', scheduleSync, { passive: true });
  document.addEventListener('touchstart', scheduleSync, { passive: true });
  document.addEventListener('touchend', scheduleSync, { passive: true });
  document.addEventListener('focusin', scheduleSync);
  document.addEventListener('focusout', scheduleSync);

  sync();

  return () => {
    scheduleChromeSync = null;
    clearIdleTimer();
    if (rafId != null) {
      cancelAnimationFrame(rafId);
    }
    observer.disconnect();
    document.removeEventListener('mousemove', scheduleSync);
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('keydown', onKeyDown, true);
    document.removeEventListener('mousedown', scheduleSync);
    document.removeEventListener('mouseup', scheduleSync);
    document.removeEventListener('touchstart', scheduleSync);
    document.removeEventListener('touchend', scheduleSync);
    document.removeEventListener('focusin', scheduleSync);
    document.removeEventListener('focusout', scheduleSync);
    document.documentElement.style.removeProperty(SHEET_CHROME_ACTIVE_VAR);
    document.documentElement.style.removeProperty(SHEET_CHROME_PE_VAR);
    document.documentElement.style.removeProperty(GRID_OPERATION_ACTIVE_VAR);
  };
}
